import { isPlatformBrowser } from '@angular/common';
import { AfterViewInit, Component, ElementRef, inject, PLATFORM_ID, signal } from '@angular/core';
import { ActivatedRoute, Router } from '@angular/router';
import { SeoService } from '../core/seo.service';
import { SiteConfigService } from '../core/site-config.service';
import { I18nService } from '../core/i18n.service';
import { BrandShowcaseService, ShowcasePerson } from '../widgets/home/brand-showcase/brand-showcase.service';

@Component({
  selector: 'app-team-detail-page',
  template: `
    <main class="profile-stage">
      @if (person(); as member) {
        <button class="profile-back" type="button" (click)="close()">← <span>{{site.text('backToTeam','Ekibimize dön')}}</span></button>
        <div class="portrait">
          @if (member.imageUrl) { <img [src]="member.imageUrl" [alt]="member.name"/> }
          @else { <strong>{{ initials(member.name) }}</strong> }
        </div>
        <section class="identity"><p>{{ member.title || site.text('specialistLabel','Uzman') }}</p><h1>{{ member.name }}</h1></section>
        <article class="biography">
          <span>{{site.text('ourSpecialist','Uzmanımız')}}</span>
          <h2>{{site.text('teamDetailTitle','Bilgi, deneyim ve güvenle yanında.')}}</h2>
          <p>{{ member.biography || site.text('teamBiographyFallback', member.name + ' hakkında ayrıntılı özgeçmiş bilgisi yakında burada yer alacak.') }}</p>
          @if (site.enabled('appointments')) { <a [href]="i18n.routePath('appointment')">{{site.text('nav_appointment','Randevu al')}}</a> }
        </article>
      } @else {
        <div class="profile-empty">{{site.text('teamProfileLoading','Ekip profili hazırlanıyor.')}}</div>
      }
    </main>
  `,
  styles: [`
    :host{display:block}.profile-stage{background:var(--paper);display:grid;gap:clamp(22px,4vw,56px);grid-template-columns:minmax(260px,.78fr) minmax(320px,1.22fr);min-height:100svh;padding:clamp(22px,5vw,74px);position:fixed;inset:0;z-index:120;overflow:auto}.profile-back{align-items:center;background:transparent;border:0;color:var(--ink);display:flex;font:900 .9rem/1 inherit;gap:10px;left:clamp(22px,5vw,74px);padding:0;position:absolute;top:clamp(22px,4vw,54px);z-index:2}.profile-back:first-letter{font-size:1.6rem}.portrait{align-items:center;align-self:end;aspect-ratio:4/5;background:color-mix(in srgb,var(--accent) 14%,var(--surface));border-radius:clamp(24px,4vw,56px);display:flex;grid-column:1;justify-content:center;overflow:hidden;transform-origin:top left}.portrait img{height:100%;object-fit:cover;width:100%}.portrait strong{color:var(--accent);font:700 clamp(4rem,12vw,10rem)/1 Georgia,serif}.identity{display:grid;gap:8px;grid-column:1}.identity p,.biography>span{color:var(--accent);font-size:.72rem;font-weight:950;letter-spacing:.16em;margin:0;text-transform:uppercase}.identity h1{font:700 clamp(2.8rem,6vw,7rem)/.88 Georgia,serif;letter-spacing:-.07em;margin:0}.biography{align-self:center;display:grid;gap:22px;grid-column:2;grid-row:1/3;max-width:780px;padding:clamp(12px,5vw,70px)}.biography h2{font:700 clamp(2.3rem,5vw,5.6rem)/.95 Georgia,serif;letter-spacing:-.06em;margin:0}.biography p{color:var(--muted);font-size:clamp(1rem,1.5vw,1.25rem);line-height:1.85;margin:0;white-space:pre-line}.biography a{background:var(--accent);border-radius:999px;color:var(--paper);font-weight:950;justify-self:start;padding:15px 24px}.profile-empty{margin:auto}
    @media(max-width:760px){.profile-stage{grid-template-columns:1fr;padding-top:82px;position:fixed}.portrait{align-self:auto;grid-column:1;max-height:48svh;width:100%}.identity{grid-column:1}.biography{grid-column:1;grid-row:auto;padding:20px 0 60px}.profile-back{background:color-mix(in srgb,var(--paper) 88%,transparent);border-radius:999px;padding:10px 14px}}
  `]
})
export class TeamDetailPage implements AfterViewInit {
  protected readonly site = inject(SiteConfigService);
  protected readonly i18n = inject(I18nService);
  private readonly data = inject(BrandShowcaseService);
  private readonly route = inject(ActivatedRoute);
  private readonly router = inject(Router);
  private readonly host = inject(ElementRef<HTMLElement>);
  private readonly platformId = inject(PLATFORM_ID);
  private readonly seo = inject(SeoService);
  protected readonly person = signal<ShowcasePerson | null>(null);
  private context?: { revert(): void };

  constructor() { void this.load(); }

  async ngAfterViewInit() {
    if (!isPlatformBrowser(this.platformId)) return;
    await this.animateIn();
  }

  protected initials(name: string) {
    return name.split(/\s+/).slice(0, 2).map((part) => part[0]).join('').toUpperCase();
  }

  protected async close() {
    if (isPlatformBrowser(this.platformId)) {
      const { gsap } = await import('gsap');
      await new Promise<void>((resolve) => {
        gsap.timeline({ onComplete: resolve })
          .to(this.host.nativeElement.querySelector('.biography'), { opacity: 0, x: 45, duration: .35 })
          .to(this.host.nativeElement.querySelector('.identity'), { opacity: 0, y: 25, duration: .25 }, '<')
          .to(this.host.nativeElement.querySelector('.portrait'), { opacity: 0, scale: .72, x: '28vw', y: '18vh', duration: .55, ease: 'power3.inOut' }, '-=.1');
      });
    }
    await this.router.navigateByUrl(this.i18n.routePath('team'));
  }

  private async load() {
    const all = await this.data.data();
    const slug = this.route.snapshot.paramMap.get('slug') || '';
    const person = all.team.find((item) => item.slug === slug) ?? null;
    this.person.set(person);
    if (person) {
      this.seo.setPage({
        title: `${person.name} | ${this.site.config().name}`,
        description: person.biography || `${person.name}, ${person.title}.`,
        path: this.router.url,
        image: person.imageUrl || undefined
      });
    }
  }

  private async animateIn() {
    const { gsap } = await import('gsap');
    const host = this.host.nativeElement;
    this.context = gsap.context(() => {
      gsap.timeline({ defaults: { ease: 'power3.out' } })
        .from('.profile-stage', { opacity: 0, duration: .25 })
        .from('.portrait', { opacity: 0, scale: .58, x: '35vw', y: '20vh', borderRadius: '50%', duration: .95, ease: 'expo.inOut' }, 0)
        .from('.identity>*', { opacity: 0, y: 28, stagger: .1, duration: .55 }, .55)
        .from('.biography>*', { opacity: 0, x: 45, stagger: .09, duration: .65 }, .65)
        .from('.profile-back', { opacity: 0, y: -12, duration: .35 }, .8);
    }, host);
  }
}
